import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useRecoilState } from "recoil";
import _ from "lodash";
import moment from "moment";
import { Box } from "@mui/material";
import {
  MyLeaveAllLeavesWithCoverage,
  MyLeavesAllLeavesCard,
} from "my-leave/components";
import { useMyLeaveEvents, useMyLeaveUsersWithRequest } from "my-leave/hooks";
import { OcInfoDialog } from "oc/components";
import { initialTableData } from "oc/configs/initial-table-data";
import {
  useOcClientState,
  useOcTableState,
  useOcCurrentUserState,
  useOcIsMobile,
  useOcRoleFilters,
} from "oc/hooks";
import { myLeaveCurrentDateState } from "states";

type MyLeaveDashboardProps = {
  role: string;
};

export const MyLeaveDashboard = ({ role }: MyLeaveDashboardProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const isMobile = useOcIsMobile();

  const [currentDate, setCurrentDate] = useRecoilState(myLeaveCurrentDateState);
  const [prevCurrentDate, setPrevCurrentDate] = useState(currentDate);
  const [showDialog, setShowDialog] = useState(false);

  let { contractCode: contract } = useOcCurrentUserState();

  const {
    roleFilters,
    prevRoleFilters,
    resetRoleFilters,
    setPrevRoleFilters,
    handleAddFilter,
    handleRemoveFilter,
  } = useOcRoleFilters({
    contract,
    product: "MyLeave",
    role: role,
  });

  const [filterable, setFilterable] = useOcClientState<boolean>(
    "allLeavesFilter",
    false,
  );

  const [filterItemErrors, setFilterItemErrors] = useState<string[]>([]);

  let tableId =
    role === "examiner" ? "allLeavesTableData" : "allLeavesViewerTableData";

  const [tableData, setTableData] = useOcTableState(tableId, {
    ...initialTableData,
    sortField: "name",
    pageSize: 5,
  });
  const [prevTableData, setPrevTableData] = useOcTableState(
    `${tableId}Prev`,
    {
      ...initialTableData,
      sortField: "name",
      pageSize: 5,
    },
  );

  const handleCompleted = () => {
    handleChangeFilterable(false);
  };

  const {
    loadQuery: loadUsers,
    data,
    loading,
  } = useMyLeaveUsersWithRequest({
    onlyRequested: false,
    roleFilters,
    tableData,
    role: role,
    dashboard: true,
    startDate: moment(currentDate).startOf("month").toDate(),
    endDate: moment(currentDate).endOf("year").add(1, "year").toDate(),
    onCompleted: handleCompleted,
  });

  const {
    loadEvents,
    data: leavesData,
    loading: leaveEventsLoading,
    eventsByDays,
  } = useMyLeaveEvents({
    roleFilters,
    viewedByAdmin: role === "examiner" ? false : true,
    currentDate: moment(currentDate).toDate(),
    contract: contract,
    role: role,
  });

  const MAX_ITEM_COUNT = 1000;
  const generateRoleFilterError = () => {
    const newState = [...filterItemErrors];
    if (roleFilters?.company && roleFilters.company.length > MAX_ITEM_COUNT) {
      newState.push(t("OC_COMPANY"));
      setFilterItemErrors(newState);
    }
    if (roleFilters?.orgUnit && roleFilters.orgUnit.length > MAX_ITEM_COUNT) {
      newState.push(t("OC_ORGUNIT"));
      setFilterItemErrors(newState);
    }

    if (
      roleFilters?.workingPlace &&
      roleFilters.workingPlace.length > MAX_ITEM_COUNT
    ) {
      newState.push(t("WORKINGPLACE"));
      setFilterItemErrors(newState);
    }
    if (
      roleFilters?.jobClassGroup &&
      roleFilters.jobClassGroup.length > MAX_ITEM_COUNT
    ) {
      newState.push(t("OC_JOBCLASSGROUP"));
      setFilterItemErrors(newState);
    }
    if (
      roleFilters?.payOffice &&
      roleFilters.payOffice.length > MAX_ITEM_COUNT
    ) {
      newState.push(t("OC_PAYOFFICE"));
      setFilterItemErrors(newState);
    }
    if (roleFilters?.contract && roleFilters.contract.length > MAX_ITEM_COUNT) {
      newState.push(t("OC_GET_EMPLOYEE"));
      setFilterItemErrors(newState);
    }

    return newState?.length > 0 ? newState : false;
  };

  const handleLoadQuery = () => {
    const isItemError = generateRoleFilterError();
    if (isItemError) {
      setShowDialog(true);
      handleChangeFilterable(false);
      return;
    }
    loadUsers();
    loadEvents();
    setPrevRoleFilters(roleFilters);
  };

  const buttonDisable = _.isEqual(roleFilters, prevRoleFilters);

  const handleChangeFilterable = (state: boolean) => {
    setFilterable(state);
  };

  const handleCurrentDateChange = (date: Date | string) => {
    setCurrentDate(date);
  };

  useEffect(() => {
    if (prevCurrentDate !== currentDate) {
      loadEvents();
      setPrevCurrentDate(currentDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate, prevCurrentDate]);

  const handleTableDataChange = (state: any) => {
    const keys = Object.keys(state).toString();
    setTableData(state);
    if (
      keys === "searchField" ||
      (keys === "searchText" && state?.searchText?.length < 3)
    ) {
      return;
    }
    loadUsers();
    loadEvents();
  };

  const handleClearFilters = () => {
    resetRoleFilters();
    handleChangeFilterable(false);
    setFilterItemErrors([]);
  };

  const handleEventSelected = (event: any) => {
    let { _id, contract } = event;
    let url = `/my-leave/user-request/MyLeave/${role}/${contract}/${_id}`;
    history.push(url);
  };

  const isTabledataChanged = !_.isEqual(tableData, prevTableData);
  useEffect(() => {
    if (isTabledataChanged) {
      handleLoadQuery();
      setPrevTableData(tableData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTabledataChanged]);

  const handleDialogClose = () => {
    setShowDialog(false);
    setFilterItemErrors([]);
  };

  const [selectedRowId, setSelectedRowId] = useState<
    string | number | undefined
  >(undefined);

  const handleRowSelection = (row: any) => {
    if (role === "assigner") {
      if (selectedRowId === undefined || selectedRowId !== row?.contract) {
        setSelectedRowId(row?.contract);
      }
    } else {
      setSelectedRowId(
        selectedRowId === row?.contract ? undefined : row?.contract,
      );
    }
  };

  useEffect(() => {
    loadUsers();
    loadEvents();
    setPrevRoleFilters(roleFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <OcInfoDialog
        open={showDialog}
        onClose={handleDialogClose}
        text={t("OC_TOO_MANY_HITS")}
        handleDialogClose={handleDialogClose}
        isMobile={isMobile}
        filterItemErrors={filterItemErrors}
      />
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        flexGrow={1}
      >
        <Box>
          <MyLeavesAllLeavesCard
            label={t("OC_MY_ROLES_EXAMINEES")}
            buttonDisable={buttonDisable}
            currentDate={currentDate}
            data={data}
            filterable={filterable}
            loading={loading}
            onAddFilter={handleAddFilter}
            onClearFilters={handleClearFilters}
            onCurrentDateChange={handleCurrentDateChange}
            onFilterableChange={handleChangeFilterable}
            onLoadQuery={handleLoadQuery}
            onRemoveFilter={handleRemoveFilter}
            onSelectRow={handleRowSelection}
            onTableDataChange={handleTableDataChange}
            product={"MyLeave"}
            role={role}
            roleFilters={roleFilters}
            tableData={tableData}
            tableId={tableId}
            contract={contract}
            selectedRowId={selectedRowId}
            onRowSelection={handleRowSelection}
            examinerDasboard={true}
            onlyRequested={false}
            onOnlyRequestedChange={() => {}}
            onShowRemoteWorksChange={() => {}}
            showRemoteWorks={false}
            handleMoreButton={() => {}}
          />
          <Box height="16px" />
          <MyLeaveAllLeavesWithCoverage
            cardTitle={t("MY_LEAVE_LEAVES_AND_REMOTE_WORKS")}
            currentDate={currentDate}
            onCurrentDateChange={handleCurrentDateChange}
            // @ts-ignore
            roleFilters={roleFilters}
            viewedByAdmin={role === "examiner" ? false : true}
            showRemoteWorks={true}
            onEventSelected={handleEventSelected}
            leavesData={leavesData}
            eventsByDays={eventsByDays}
            loading={leaveEventsLoading}
            role={role}
          />
        </Box>
      </Box>
    </>
  );
};

export default MyLeaveDashboard;
